import React, { useCallback, useMemo, useState } from 'react';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { InlineMessage, SectionCard, StatusChip } from './ui';
import type { StoreRebateAttestation } from '../services/mapPricingApi';

export interface AttestationDraft {
  kind: StoreRebateAttestation['kind'];
  vendorReference: string;
  note: string;
  agreed: boolean;
}

const EMPTY_DRAFT: AttestationDraft = { kind: 'vendor_program', vendorReference: '', note: '', agreed: false };

/**
 * Flag `store_attested_rebates`: the attestation a store gives when it keeps a
 * rebate sheet to itself before the brand's vendor is on Motzu. `visible` is
 * flag on AND "Keep this pricing for my store only" ticked; while visible the
 * upload stays blocked until the confirmation is ticked, and `payload` is the
 * JSON sent as the `attestation` multipart field (null when not visible).
 */
export function useStoreRebateAttestation(privateToStore: boolean) {
  const { enabled } = useOrgFeatureFlag('store_attested_rebates');
  const [draft, setDraft] = useState<AttestationDraft>(EMPTY_DRAFT);
  const visible = enabled && privateToStore;
  const blocked = visible && !draft.agreed;
  const payload = useMemo(
    () =>
      visible && draft.agreed
        ? JSON.stringify({
            kind: draft.kind,
            ...(draft.kind === 'vendor_program' && draft.vendorReference.trim()
              ? { vendorReference: draft.vendorReference.trim() }
              : {}),
            ...(draft.note.trim() ? { note: draft.note.trim() } : {}),
            agreed: true,
          })
        : null,
    [visible, draft],
  );
  const update = useCallback((patch: Partial<AttestationDraft>) => setDraft((d) => ({ ...d, ...patch })), []);
  return { visible, blocked, payload, draft, update };
}

interface BlockProps {
  draft: AttestationDraft;
  onChange: (patch: Partial<AttestationDraft>) => void;
}

/** The attestation form on the MAP Pricing Data upload card. */
export const StoreRebateAttestationBlock: React.FC<BlockProps> = ({ draft, onChange }) => {
  const { t } = useAppTranslation();
  return (
    <SectionCard title={t('store.attestedRebates.title')} sx={{ mb: 3 }}>
      <InlineMessage severity="info">{t('store.attestedRebates.intro')}</InlineMessage>
      <Stack spacing={2}>
        <FormControl>
          <FormLabel id="store-attestation-kind" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
            {t('store.attestedRebates.title')}
          </FormLabel>
          <RadioGroup
            aria-labelledby="store-attestation-kind"
            value={draft.kind}
            onChange={(e) => onChange({ kind: e.target.value as AttestationDraft['kind'] })}
          >
            <FormControlLabel value="vendor_program" control={<Radio />} label={t('store.attestedRebates.kindVendor')} />
            <FormControlLabel value="store_funded" control={<Radio />} label={t('store.attestedRebates.kindStore')} />
          </RadioGroup>
        </FormControl>
        {draft.kind === 'vendor_program' && (
          <TextField
            size="small"
            label={t('store.attestedRebates.reference')}
            value={draft.vendorReference}
            onChange={(e) => onChange({ vendorReference: e.target.value })}
            inputProps={{ maxLength: 120 }}
          />
        )}
        <TextField
          size="small"
          multiline
          minRows={2}
          label={t('store.attestedRebates.note')}
          value={draft.note}
          onChange={(e) => onChange({ note: e.target.value })}
          inputProps={{ maxLength: 500 }}
        />
        <FormControlLabel
          control={<Checkbox checked={draft.agreed} onChange={(e) => onChange({ agreed: e.target.checked })} />}
          label={t('store.attestedRebates.agree')}
        />
        {!draft.agreed && (
          <Typography variant="caption" color="text.secondary">
            {t('store.attestedRebates.uploadBlocked')}
          </Typography>
        )}
      </Stack>
    </SectionCard>
  );
};

/** "Store attested" chip; hovering says who attested, when and what they said. */
export const StoreAttestedChip: React.FC<{ attestation?: StoreRebateAttestation | null }> = ({ attestation }) => {
  const { t } = useAppTranslation();
  if (!attestation) return null;
  const date = attestation.attestedAt ? new Date(attestation.attestedAt).toLocaleDateString() : '';
  const detail = [
    attestation.vendorReference ? ` (${attestation.vendorReference})` : '',
    attestation.note ? ` — ${attestation.note}` : '',
  ].join('');
  const tip = t('store.attestedRebates.tooltip', {
    name: attestation.attestedByName || t('store.attestedRebates.someone'),
    date,
    kind: t(`store.attestedRebates.kindLabel.${attestation.kind}`),
    detail,
  });
  return (
    <Tooltip title={tip} arrow>
      <span>
        <StatusChip label={t('store.attestedRebates.chip')} tone="info" size="small" />
      </span>
    </Tooltip>
  );
};
